'use client';

import { Typography } from '@v2/ui';
import { Slider, SliderRange, SliderThumb, SliderTrack } from '@v2/ui/Slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@v2/ui/Tooltip';
import { cn } from '@v2/utils';
import { useEffect, useState } from 'react';

interface SliderFilterProps {
  label?: string;
  min: number;
  max: number;
  defaultValue?: number[];
  value?: number[];
  onValueChange?: (value: number[]) => void;
  className?: string;
  unit?: string;
  step?: number;
  showThumbs?: 'single' | 'double';
  formatValue?: (value: number) => string;
}

export function SliderFilter({
  label,
  min,
  max,
  defaultValue,
  value,
  onValueChange,
  className,
  unit = '',
  step = 1,
  showThumbs = 'single',
  formatValue = (val) => `${val}${unit}`,
}: SliderFilterProps) {
  const [currentValue, setCurrentValue] = useState(
    value || defaultValue || [min]
  );

  useEffect(() => {
    if (value) {
      setCurrentValue(value);
    }
  }, [value]);

  const finiteThumbs = currentValue.filter((thumb) => Number.isFinite(thumb));
  const sliderValue =
    showThumbs === 'single'
      ? [finiteThumbs.length ? Math.max(...finiteThumbs) : min]
      : currentValue;

  const handleValueChange = (newValue: number[]) => {
    const nextThumbs = newValue.filter((thumb) => Number.isFinite(thumb));
    const next =
      showThumbs === 'single'
        ? [nextThumbs.length ? Math.max(...nextThumbs) : min]
        : newValue;
    setCurrentValue(next);
    onValueChange?.(next);
  };

  return (
    <div className={cn('flex flex-col gap-y-3', className)}>
      {label ? (
        <Typography size="tmd" className="font-bold">
          {label}
        </Typography>
      ) : null}

      <div>
        <Typography
          size="tmd"
          className="mb-1.5 flex items-center justify-between font-medium"
        >
          <span>{formatValue(min)}</span>
          <span>{formatValue(max)}</span>
        </Typography>

        <Slider
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          onValueChange={handleValueChange}
          defaultValue={defaultValue}
        >
          <SliderTrack>
            <SliderRange />
          </SliderTrack>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <SliderThumb />
              </TooltipTrigger>
              <TooltipContent>{formatValue(sliderValue[0])}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {showThumbs === 'double' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SliderThumb />
                </TooltipTrigger>
                <TooltipContent>
                  {formatValue(currentValue[1] || currentValue[0])}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Slider>
      </div>
    </div>
  );
}
